import { supabase } from '../supabaseClient';
import {
  emptyAssignmentPointsNoteDoc,
  type AssignmentPointsNoteDoc,
  type AssignmentPointsItem,
} from '../types/assignmentPointsNote';
import {
  loadAssignmentPointsNoteCache,
  normalizeAssignmentPointsNoteDoc,
  saveAssignmentPointsNoteCache,
} from '../utils/assignmentPointsNoteStorage';
import {
  publishAssignmentPointsNoteDoc,
  resolveAssignmentPointsNoteFromCacheOrMainTab,
} from '../utils/assignmentPointsNoteTabBridge';
import { isMainPresentationBrowserTab } from '../utils/noteDocTabBridgeCore';
import { notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import {
  deleteAssignmentRewardsForItem,
  syncAssignmentPointsToTable,
  type AssignmentPointSyncInput,
} from './classMemberOwnedDataService';

function docId(workspaceId: string, gradeId: string): string {
  return `apn-${workspaceId}-${gradeId}`;
}

export async function getAssignmentPointsNote(
  workspaceId: string,
  gradeId: string
): Promise<AssignmentPointsNoteDoc> {
  if (!workspaceId || !gradeId) return emptyAssignmentPointsNoteDoc(workspaceId, gradeId);

  const cached = loadAssignmentPointsNoteCache(workspaceId, gradeId);
  if (cached) return cached;

  /* 메인 프레젠: LS/메인 탭 캐시만 — miss여도 REST 금지 (메인 탭이 워밍) */
  if (isMainPresentationBrowserTab()) {
    const bridged = await resolveAssignmentPointsNoteFromCacheOrMainTab(
      workspaceId,
      gradeId
    );
    if (bridged) return bridged;
    console.warn('[메인프레젠·캐시] 과제노트 캐시 미스', { workspaceId, gradeId });
    return emptyAssignmentPointsNoteDoc(workspaceId, gradeId);
  }

  const { data, error } = await supabase
    .from('assignment_point_notes')
    .select('payload, updated_at')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .maybeSingle();

  if (error) {
    if (/assignment_point_notes|does not exist|schema cache/i.test(error.message ?? '')) {
      return emptyAssignmentPointsNoteDoc(workspaceId, gradeId);
    }
    throw new Error(error.message);
  }

  if (!data) {
    const empty = emptyAssignmentPointsNoteDoc(workspaceId, gradeId);
    saveAssignmentPointsNoteCache(empty);
    return empty;
  }

  const doc = normalizeAssignmentPointsNoteDoc(
    { ...(data.payload as object), updatedAt: data.updated_at },
    workspaceId,
    gradeId
  );
  saveAssignmentPointsNoteCache(doc);
  return doc;
}

/** items에서 과제 체크 이력을 추출. groupId 지정 시 해당 그룹 멤버만. */
function extractAssignmentPointInputs(
  items: AssignmentPointsItem[],
  memberGroupMap: Map<string, string>,
  onlyGroupId?: string
): AssignmentPointSyncInput[] {
  const out: AssignmentPointSyncInput[] = [];

  for (const item of items) {
    if (item.kind === 'general') {
      for (const st of item.studentStatuses) {
        const groupId = memberGroupMap.get(st.studentId);
        if (!groupId) continue;
        if (onlyGroupId && groupId !== onlyGroupId) continue;
        out.push({
          memberId: st.studentId,
          groupId,
          assignmentItemId: item.id,
          assignmentKind: 'general',
          assignmentTitle: item.title,
          status: st.status,
          earnedPoints: st.earnedPoints ?? 0,
          earnedCoins: st.earnedCoins ?? 0,
          earnedItemIds: st.earnedItemIds ?? [],
          recordedDate: item.dueDate,
          memo: st.memo,
        });
      }
    } else if (item.kind === 'project') {
      const subIds = new Set(item.subTasks.map((s) => s.id));
      for (const [key, st] of Object.entries(item.studentStatusesBySubTask)) {
        if (!st) continue;
        const sep = key.indexOf(':');
        const subId = sep >= 0 ? key.slice(0, sep) : key;
        const memberId = sep >= 0 ? key.slice(sep + 1) : st.studentId;
        if (!subIds.has(subId)) continue;
        const groupId = memberGroupMap.get(memberId);
        if (!groupId) continue;
        if (onlyGroupId && groupId !== onlyGroupId) continue;
        const sub = item.subTasks.find((s) => s.id === subId);
        out.push({
          memberId,
          groupId,
          assignmentItemId: item.id,
          assignmentSubId: subId,
          assignmentKind: 'project',
          assignmentTitle: `${item.title} · ${sub?.title ?? ''}`,
          status: st.status,
          earnedPoints: st.earnedPoints ?? 0,
          earnedCoins: st.earnedCoins ?? 0,
          earnedItemIds: st.earnedItemIds ?? [],
          recordedDate: item.dueDate,
          memo: st.memo,
        });
      }
    } else if (item.kind === 'routine') {
      for (const bucket of item.dateStatuses) {
        for (const st of bucket.studentStatuses) {
          const groupId = memberGroupMap.get(st.studentId);
          if (!groupId) continue;
          if (onlyGroupId && groupId !== onlyGroupId) continue;
          out.push({
            memberId: st.studentId,
            groupId,
            assignmentItemId: item.id,
            assignmentSubId: bucket.date,
            assignmentKind: 'routine',
            assignmentTitle: item.title,
            status: st.status,
            earnedPoints: st.earnedPoints ?? 0,
            earnedCoins: st.earnedCoins ?? 0,
            earnedItemIds: st.earnedItemIds ?? [],
            recordedDate: bucket.date,
            memo: st.memo,
          });
        }
      }
    }
  }

  return out;
}

export type SaveAssignmentPointsNoteOpts = {
  /** 있으면 class_members 재조회 생략 */
  memberGroupMap?: Map<string, string>;
  /**
   * rewards 테이블 동기화 대상 과제 ID.
   * - 생략: 전체 items 동기화 + orphan 삭제(멤버 purge 등) — 클래스(학년) 단위
   * - []: rewards 동기화 안 함
   * - [id…]: 해당 과제만 (가능하면 그룹 스코프)
   */
  syncRewardItemIds?: string[];
  /** 삭제된 과제 — rewards 행만 삭제 */
  deletedRewardItemIds?: string[];
  /**
   * 과제 단위로 전 그룹 upsert + 문서에 없는 하위과제/날짜 행 삭제.
   * syncRewardGroupId보다 우선한다.
   */
  replaceRewardItemIds?: string[];
  /** 그룹 단위 rewards 동기화·orphan 정리 */
  syncRewardGroupId?: string;
  /** true면 workspaces 버전 bump 생략 */
  skipNotify?: boolean;
};

export async function saveAssignmentPointsNote(
  doc: AssignmentPointsNoteDoc,
  opts?: SaveAssignmentPointsNoteOpts
): Promise<void> {
  if (!doc.workspaceId || !doc.gradeId) return;
  const updatedAt = new Date().toISOString();
  const payload = {
    settings: doc.settings,
    items: doc.items,
    coverImages: doc.coverImages ?? [],
    updatedAt,
  };
  const row = {
    id: docId(doc.workspaceId, doc.gradeId),
    workspace_id: doc.workspaceId,
    grade_id: doc.gradeId,
    payload,
    updated_at: updatedAt,
  };

  const { error } = await supabase.from('assignment_point_notes').upsert(row, { onConflict: 'id' });
  if (error) {
    if (/assignment_point_notes|does not exist|schema cache/i.test(error.message ?? '')) {
      const next = { ...doc, updatedAt };
      saveAssignmentPointsNoteCache(next);
      publishAssignmentPointsNoteDoc(next);
      return;
    }
    throw new Error(error.message);
  }

  const next = { ...doc, updatedAt };
  saveAssignmentPointsNoteCache(next);
  publishAssignmentPointsNoteDoc(next);

  if (opts?.deletedRewardItemIds?.length) {
    for (const itemId of opts.deletedRewardItemIds) {
      await deleteAssignmentRewardsForItem(doc.workspaceId, doc.gradeId, itemId);
    }
  }

  const replaceIds = opts?.replaceRewardItemIds;
  const syncIds = opts?.syncRewardItemIds;
  const skipRewardSync =
    !replaceIds?.length && Array.isArray(syncIds) && syncIds.length === 0;
  if (!skipRewardSync) {
    let memberGroupMap = opts?.memberGroupMap;
    if (!memberGroupMap) {
      const { data: members } = await supabase
        .from('class_members')
        .select('id, member_group_id')
        .eq('workspace_id', doc.workspaceId)
        .eq('grade_id', doc.gradeId);
      memberGroupMap = new Map<string, string>();
      (members as Array<{ id: string; member_group_id: string | null }> | null)?.forEach((m) => {
        if (m.member_group_id) memberGroupMap!.set(m.id, m.member_group_id);
      });
    }

    if (replaceIds && replaceIds.length > 0) {
      const itemsForSync = doc.items.filter((item) => replaceIds.includes(item.id));
      const inputs = extractAssignmentPointInputs(itemsForSync, memberGroupMap);
      await syncAssignmentPointsToTable(doc.workspaceId, doc.gradeId, inputs, {
        replaceAssignmentItemIds: replaceIds,
      });
    } else {
      const itemsForSync =
        syncIds == null
          ? doc.items
          : doc.items.filter((item) => syncIds.includes(item.id));
      const onlyGroupId = opts?.syncRewardGroupId;
      const inputs = extractAssignmentPointInputs(itemsForSync, memberGroupMap, onlyGroupId);

      if (syncIds != null && syncIds.length === 1 && onlyGroupId) {
        // 그룹 단위: 해당 과제·그룹만 upsert + orphan 정리
        await syncAssignmentPointsToTable(doc.workspaceId, doc.gradeId, inputs, {
          replaceScope: { assignmentItemId: syncIds[0], groupId: onlyGroupId },
        });
      } else if (syncIds == null) {
        // 클래스(학년) 단위 전체 동기화 — 멤버 purge 등
        await syncAssignmentPointsToTable(doc.workspaceId, doc.gradeId, inputs, {
          allAssignmentItemIds: doc.items.map((item) => item.id),
        });
      } else if (inputs.length > 0) {
        await syncAssignmentPointsToTable(doc.workspaceId, doc.gradeId, inputs, {});
      }
    }
  }

  if (!opts?.skipNotify) {
    await notifySchoolProgramMutation(doc.workspaceId, ['pointNotes']);
  }
}
